import type {
  PortalAssetLifecycle,
  PortalAssetMediaType,
  PortalAssetReviewStatus,
} from '../../types/portalAsset.js';
import { localDayStart, narrowMode, searchState } from './sortSearchView.js';
import type { SortOptionId } from './sortOptions.js';

// THE STAFF CROSS-ACCOUNT LIST'S FORM STATE → QUERY (2026-10-09). PURE.
//
// ⚠️ BRAND, GROUP, MANAGER AND CLUB STACK (2026-10-12). The old "brand or club, never both"
// rule is gone with the stored-field query it protected: they now resolve together,
// server-side, to a set of clubs (rosterFilter.ts). "Snap clubs Jo manages in NSW North"
// is one request. A combination that matches no club is answered with `noMatchingClubs`,
// which the page words differently from "no assets".
// ⚠️ A CATEGORY BELONGS TO A BRAND (categories are per brand), so it is only
// meaningful — and only sent — alongside a brand, and changing the brand clears it:
// a stale category id from the previous brand would silently filter to nothing.
// ⚠️ DATES are the viewer's LOCAL calendar days: `from` is the start of that day and
// `to` is the start of the day AFTER the chosen one (the server's bound is
// exclusive), so picking 8 Oct through 8 Oct means all of 8 Oct.

export type StaffAllForm = {
  brandAccountId: string;
  /** A group-type account: the clubs whose NEAREST group it is. */
  groupAccountId: string;
  /** A staff user (uid): the clubs they manage. */
  accountManagerId: string;
  accountId: string;
  /** One of the chosen brand's categories. ⚠️ Excludes `categoryKind`: filtering is one category OR one type at a time. */
  categoryId: string;
  /** One category TYPE (e.g. team), across every brand. */
  categoryKind: string;
  status: PortalAssetReviewStatus | '';
  mediaType: PortalAssetMediaType | '';
  lifecycle: PortalAssetLifecycle | '';
  /** YYYY-MM-DD from a date input, or ''. */
  from: string;
  to: string;
  /** One of the four named sorts. ⚠️ Name / Largest clear the date range (chooseSort). */
  sort: SortOptionId;
  /** The raw search box. ⚠️ A usable query sends the SCOPE ALONE (see sortSearchView.ts). */
  search: string;
  /** "Not reviewed by Kyck" — also scope alone (see sortSearchView.narrowMode). */
  kyckUnreviewed: boolean;
};

export const EMPTY_STAFF_ALL_FORM: StaffAllForm = {
  brandAccountId: '',
  groupAccountId: '',
  accountManagerId: '',
  accountId: '',
  categoryId: '',
  categoryKind: '',
  status: '',
  mediaType: '',
  lifecycle: '',
  from: '',
  to: '',
  sort: 'newest',
  search: '',
  kyckUnreviewed: false,
};

export function chooseBrand(form: StaffAllForm, brandAccountId: string): StaffAllForm {
  return { ...form, brandAccountId, categoryId: '' };
}

/** Choosing a category clears the kind and vice versa: two array-contains clauses cannot share a query. */
export function chooseCategory(form: StaffAllForm, categoryId: string): StaffAllForm {
  return { ...form, categoryId, categoryKind: categoryId ? '' : form.categoryKind };
}

export function chooseCategoryKind(form: StaffAllForm, categoryKind: string): StaffAllForm {
  return { ...form, categoryKind, categoryId: categoryKind ? '' : form.categoryId };
}

export function chooseClub(form: StaffAllForm, accountId: string): StaffAllForm {
  return { ...form, accountId };
}

export type StaffAllParams = {
  brandAccountId?: string;
  groupAccountId?: string;
  accountManagerId?: string;
  accountId?: string;
  categoryId?: string;
  categoryKind?: string;
  status?: PortalAssetReviewStatus;
  mediaType?: PortalAssetMediaType;
  lifecycle?: PortalAssetLifecycle;
  from?: string;
  to?: string;
  sort?: SortOptionId;
  search?: string;
  kyckUnreviewed?: true;
};

/** Only defined values; an unreadable date is dropped rather than sent. */
export function staffAllParams(form: StaffAllForm): StaffAllParams {
  const p: StaffAllParams = {};
  if (form.brandAccountId) p.brandAccountId = form.brandAccountId;
  if (form.groupAccountId) p.groupAccountId = form.groupAccountId;
  if (form.accountManagerId) p.accountManagerId = form.accountManagerId;
  if (form.accountId) p.accountId = form.accountId;
  // ⚠️ A running search resolves with the SCOPE above and nothing else (sortSearchView.ts): the rest stay in the form
  // (disabled on screen) and are simply not sent, so clearing the search restores the page as it was.
  const s = searchState(form.search);
  if (s.active) return { ...p, search: s.query };
  if (narrowMode(form) === 'unreviewed') return { ...p, kyckUnreviewed: true };
  if (form.categoryId && form.brandAccountId) p.categoryId = form.categoryId;
  // ⚠️ A kind is agency-wide: sent with or without a brand. And never beside a category id.
  else if (form.categoryKind) p.categoryKind = form.categoryKind;
  if (form.status) p.status = form.status;
  if (form.mediaType) p.mediaType = form.mediaType;
  if (form.lifecycle) p.lifecycle = form.lifecycle;
  if (form.sort !== 'newest') p.sort = form.sort;
  const from = localDayStart(form.from);
  const to = localDayStart(form.to, 1);
  if (from) p.from = from;
  if (to) p.to = to;
  return p;
}

/** True when the chosen range is upside down — the server would 400, the page says so instead. */
export function dateRangeIsBackwards(form: StaffAllForm): boolean {
  const { from, to } = staffAllParams(form);
  return from !== undefined && to !== undefined && Date.parse(from) >= Date.parse(to);
}

/** ⚠️ The sort is not a filter: choosing one never shows "Clear filters" or turns the empty sentence into "nothing matches". */
export function hasAnyStaffAllFilter(form: StaffAllForm): boolean {
  const { sort: _sort, ...rest } = staffAllParams(form);
  return Object.keys(rest).length > 0;
}

export const MEDIA_TYPE_LABELS: Record<PortalAssetMediaType, string> = { image: 'Photos', video: 'Videos' };

export const LIFECYCLE_LABELS: Record<PortalAssetLifecycle, string> = {
  uploading: 'Uploading',
  processing: 'Processing',
  ready: 'Ready',
  failed: 'Failed',
  awaiting_support: 'Video queued',
};

/**
 * ⚠️ THREE DIFFERENT EMPTIES. No clubs matched the group / manager / brand / club filters
 * (`noMatchingClubs`), clubs matched but nothing matches the remaining filters, and nothing
 * has been uploaded at all. Collapsing them says "no assets" about a roster question.
 */
export function staffAllEmptySentence(filtered: boolean, noMatchingClubs = false): string {
  if (noMatchingClubs) return 'No clubs match those filters.';
  return filtered ? 'Nothing matches these filters.' : 'No assets have been uploaded yet.';
}
